import { useEffect, useRef, useState } from 'react';
import { PortalApiError, safeErrorMessage } from '@/lib/portal-api';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { moneyOperationSession } from '../money/money-operation-session';
import { useConsultationIdentity } from './use-consultation-identity';
import { AcButton, ErrorState, LoadingState } from '@/dashboard/ui';
import {
  fetchPaymentAllocationStatus, relinkPatientPaymentApi, unlinkPatientPaymentApi,
  type PaymentAllocationResult, type PaymentAllocationStatus,
} from '@/dashboard/api';
import { formatMinorUnitsInr, type PatientPaymentRow } from './billing-entry-view';

export type AllocationIntent = {
  operationId: string; paymentId: number; appointmentId: number; clinicId: number;
  kind: 'attach' | 'undo';
};
type Phase = 'pending' | 'refused' | 'unknown' | 'checking' | 'denied' | 'saved';
export type AllocationRecoveryState = {
  intent: AllocationIntent; phase: Phase; message: string; reloadSafe: boolean;
  currentPayment?: PatientPaymentRow;
  supportReference?: string;
};
type Dependencies = {
  write: (intent: AllocationIntent) => Promise<PaymentAllocationResult>;
  check: (intent: AllocationIntent) => Promise<PaymentAllocationStatus>;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  changed: (state: AllocationRecoveryState | null) => void;
  committed: (payment: PatientPaymentRow, intent: AllocationIntent) => void;
  denied: (isCurrent: () => boolean) => void;
};
const UNKNOWN = 'The attachment may have changed. Check attachment before another attempt. Amounts remain the last confirmed figures.';
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function validIntent(value: unknown): value is AllocationIntent {
  if (!value || typeof value !== 'object') return false;
  const i = value as AllocationIntent;
  return uuid.test(i.operationId) && (i.kind === 'attach' || i.kind === 'undo') &&
    [i.paymentId, i.appointmentId, i.clinicId].every(n => Number.isSafeInteger(n) && n > 0);
}

/** One unresolved intent per actor/org. Persist identifiers only, never payment data.
 * Reload always checks: even a locally refused attempt could have been retried
 * before the browser closed. Missing receipts never authorize a second write. */
export class AllocationRecovery {
  state: AllocationRecoveryState | null = null;
  private active = true;
  constructor(private key: string, private deps: Dependencies) {
    const raw = deps.storage?.getItem(key);
    const intent: unknown = raw ? JSON.parse(raw) : null;
    if (raw && !validIntent(intent)) throw new Error('Saved attachment recovery cannot be read. Do not start another attachment.');
    if (validIntent(intent)) this.state = { intent, phase: 'unknown', message: UNKNOWN, reloadSafe: true };
  }
  private publish(state: AllocationRecoveryState | null) {
    this.state = state;
    if (this.active) this.deps.changed(state);
  }
  private persist(intent: AllocationIntent): boolean {
    try {
      if (!this.deps.storage) return false;
      this.deps.storage.setItem(this.key, JSON.stringify(intent));
      return this.deps.storage.getItem(this.key) === JSON.stringify(intent);
    } catch { return false; }
  }
  retire(clear: boolean) {
    this.active = false;
    if (clear && (this.state?.phase === 'saved' || this.state?.phase === 'refused')) {
      try { this.deps.storage?.removeItem(this.key); } catch { /* Already inaccessible. */ }
    }
  }
  dismiss() {
    if (this.state?.phase !== 'saved' && this.state?.phase !== 'refused') return;
    try { this.deps.storage?.removeItem(this.key); } catch { /* Keep memory usable. */ }
    this.publish(null);
  }
  async start(intent: AllocationIntent) {
    if (!this.active || !validIntent(intent) || (this.state && this.state.phase !== 'saved')) return;
    this.publish({ intent, phase: 'pending', message: '', reloadSafe: false });
    await this.write();
  }
  async retry() {
    if (!this.active || this.state?.phase !== 'refused') return;
    this.publish({ ...this.state, phase: 'pending', message: '', supportReference: undefined });
    await this.write();
  }
  private failure(error: unknown, checking: boolean) {
    if (!this.active || !this.state) return;
    const denied = error instanceof PortalApiError && [401, 403].includes(error.status);
    const refused = !checking && error instanceof PortalApiError &&
      ['PAYMENT_ATTACHMENT_NOT_SAVED', 'PAYMENT_ATTACHMENT_UNDO_NOT_SAVED'].includes(error.code ?? '');
    // Preserve only the explicitly controlled allocation error messages on 5xx.
    const controlled = error instanceof PortalApiError &&
      ['PAYMENT_ATTACHMENT_NOT_SAVED', 'PAYMENT_ATTACHMENT_UNDO_NOT_SAVED',
        'PAYMENT_ATTACHMENT_OUTCOME_UNKNOWN', 'PAYMENT_ATTACHMENT_UNDO_OUTCOME_UNKNOWN'].includes(error.code ?? '');
    this.publish({ ...this.state, phase: denied ? 'denied' : refused ? 'refused' : 'unknown',
      message: controlled ? error.message : safeErrorMessage(error, UNKNOWN),
      supportReference: controlled ? error.message.match(/PAY-AUD-[a-zA-Z0-9-]+/)?.[0] ?? this.state.supportReference : this.state.supportReference });
    if (denied) this.deps.denied(() => this.active);
  }
  private async write() {
    const state = this.state!;
    if (!this.persist(state.intent)) {
      this.publish({ ...state, phase: 'refused', reloadSafe: false,
        message: 'Recovery could not be saved. Nothing was sent. Restore storage before retrying.' });
      return;
    }
    this.publish({ ...state, reloadSafe: true });
    try {
      const result = await this.deps.write(state.intent);
      if (!this.active) return;
      if (result.operationId !== state.intent.operationId || !Number.isSafeInteger(result.auditId) ||
          result.payment?.id !== state.intent.paymentId) throw new Error('Invalid allocation receipt');
      // A replay receipt is historical. Always read current state before showing
      // an attachment or refreshing amounts as confirmed.
      await this.check(true);
    } catch (error) { this.failure(error, false); }
  }
  async check(afterWrite = false) {
    if (!this.active || !this.state || this.state.phase === 'checking' || this.state.phase === 'saved' || (this.state.phase === 'pending' && !afterWrite)) return;
    const intent = this.state.intent;
    this.publish({ ...this.state, phase: 'checking', message: UNKNOWN });
    try {
      const result = await this.deps.check(intent);
      if (!this.active || !this.state) return;
      if (result.operationId !== intent.operationId) throw new Error('Mismatched receipt');
      if (result.outcome === 'unknown') {
        this.publish({ ...this.state, phase: 'unknown', message: UNKNOWN });
        return;
      }
      if (result.outcome !== 'committed' || result.operationKind !== intent.kind || result.appointmentId !== intent.appointmentId ||
          !Number.isSafeInteger(result.auditId) || result.currentPayment?.id !== intent.paymentId ||
          result.currentPayment.clinicId !== intent.clinicId ||
          !Number.isSafeInteger(result.currentPayment.amountMinorUnits) || result.currentPayment.amountMinorUnits < 0 ||
          (result.currentPayment.appointmentId !== null && (!Number.isSafeInteger(result.currentPayment.appointmentId) || result.currentPayment.appointmentId <= 0))) throw new Error('Mismatched attachment status');
      const payment = result.currentPayment;
      const current = payment.voidedAt ? 'The payment is currently voided.' :
        payment.appointmentId === null ? 'The payment is currently unattached.' :
          `The payment is currently attached to appointment #${payment.appointmentId}.`;
      this.publish({ ...this.state, phase: 'saved', currentPayment: payment,
        message: `${intent.kind === 'undo' ? 'Undo' : 'Attachment'} for appointment #${intent.appointmentId} was saved with its history. ${current}` });
      try { this.deps.storage?.removeItem(this.key); } catch { /* Reload may safely check again. */ }
      this.deps.committed(payment, intent);
    } catch (error) { this.failure(error, true); }
  }
}

/** Uses the existing auth session, not /me additions or a second auth model.
 * Development-only token auth gets an opaque digest; no token is persisted here. */
export function useAllocationRecovery(organizationId: number | null, callbacks: {
  committed: Dependencies['committed']; denied: Dependencies['denied'];
}) {
  const [state, setState] = useState<AllocationRecoveryState | null>(null);
  const [ready, setReady] = useState(false);
  const controller = useRef<AllocationRecovery | null>(null);
  const latest = useRef(callbacks);
  latest.current = callbacks;
  const identity = useConsultationIdentity();
  const [bindingError, setBindingError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let sequence = 0;
    setReady(false); setState(null); setBindingError(null);
    const supabase = getSupabaseBrowserClient();
    const bind = async () => {
      const ticket = ++sequence;
      controller.current?.retire(false);
      controller.current = null;
      setReady(false); setState(null); setBindingError(null);
      if (!organizationId) return;
      try {
        const session = await moneyOperationSession('allocation');
        if (cancelled || ticket !== sequence) return;
        if (session.organizationId !== organizationId) throw new Error('Money scope changed. Reopen this payment.');
        const storage = window.sessionStorage;
        // Older versions stored only actor/org identifiers. Preserve and migrate
        // that check-only intent; never delete unknown work on a session change.
        const legacyKey = `payment-allocation:${organizationId}:${session.actor}`;
        const legacy = storage.getItem(legacyKey);
        if (legacy) {
          const existing = storage.getItem(session.key);
          if (existing && existing !== legacy) throw new Error('Two saved attachment requests need recovery before another write.');
          storage.setItem(session.key, legacy);
          if (storage.getItem(session.key) !== legacy) throw new Error('Saved attachment recovery could not be preserved.');
          storage.removeItem(legacyKey);
        }
        const next = new AllocationRecovery(session.key, {
          storage, changed: setState,
          write: async i => {
            await session.assertCurrent();
            const result = await (i.kind === 'attach' ? relinkPatientPaymentApi : unlinkPatientPaymentApi)(i.paymentId, i.appointmentId, i.operationId, session.bearer);
            await session.assertCurrent();
            return result;
          },
          check: async i => {
            await session.assertCurrent();
            const result = await fetchPaymentAllocationStatus(i.paymentId, i.operationId, session.bearer);
            await session.assertCurrent();
            return result;
          },
          committed: (p, i) => latest.current.committed(p, i),
          denied: isCurrent => latest.current.denied(isCurrent),
        });
        if (cancelled || ticket !== sequence) { next.retire(false); return; }
        controller.current = next; setState(next.state); setReady(true);
      } catch (error) {
        if (!cancelled && ticket === sequence) setBindingError(error instanceof Error ? error.message : 'Attachment recovery is unavailable.');
      }
    };
    void bind();
    const subscription = supabase?.auth.onAuthStateChange(() => { void bind(); }).data.subscription;
    return () => { cancelled = true; sequence++; controller.current?.retire(false); controller.current = null; subscription?.unsubscribe(); };
  }, [organizationId, identity]);
  return { state, ready, ...(bindingError ? {bindingError} : {}),
    start: (intent: Omit<AllocationIntent, 'operationId'>) => controller.current?.start({ ...intent, operationId: crypto.randomUUID() }),
    retry: () => controller.current?.retry(), check: () => controller.current?.check(),
    dismiss: () => controller.current?.dismiss(),
  };
}

export function AllocationRecoveryPanel({ recovery, payment }: { recovery: ReturnType<typeof useAllocationRecovery>; payment?: PatientPaymentRow }) {
  const s = recovery.state;
  if (!s) return recovery.bindingError ? <ErrorState title="Attachment recovery unavailable" detail={recovery.bindingError} /> : null;
  const busy = s.phase === 'pending' || s.phase === 'checking';
  const known = s.currentPayment ?? payment;
  const position = known ? (known.appointmentId === null ? 'not attached to any visit' : `attached to visit #${known.appointmentId}`) : 'unavailable';
  return <section className="ac-section" aria-label="Payment attachment status" data-testid="allocation-recovery">
    <div className="ac-section__head"><h3 className="ac-section__title">Payment #{s.intent.paymentId}{known ? ` / ${formatMinorUnitsInr(known.amountMinorUnits)}` : ''}</h3></div>
    <p>Target: visit #{s.intent.appointmentId}<br />
      {s.phase === 'saved' ? 'Current' : 'Last confirmed'}: {position}. No money collected or refunded.</p>
    {s.supportReference && <p>Ref {s.supportReference}</p>}
    {!s.reloadSafe && <p role="alert">Keep this page open. Recovery is not saved across reloads.</p>}
    {busy ? <><LoadingState label={s.phase === 'pending' ? 'Saving attachment...' : 'Checking attachment...'} /><div><AcButton variant="secondary" disabled>{s.phase === 'pending' ? 'Saving' : 'Checking'}</AcButton></div></> :
      s.phase === 'saved' ? <><p role="status">{s.intent.kind === 'undo' ? 'Payment removed from visit' : 'Payment attached to visit'}</p><div><AcButton variant="secondary" onClick={recovery.dismiss}>Done</AcButton></div></> :
      <ErrorState title={s.phase === 'refused' ? 'Attachment not saved' : s.phase === 'denied' ? 'Access denied' : 'Attachment not confirmed'}
        detail={s.phase === 'refused' ? 'Previous confirmed attachment retained.' : s.phase === 'denied' ? 'Billing permission required.' : 'Check before another attempt.'}
        action={s.phase === 'refused' ? <><AcButton onClick={recovery.retry}>Try {s.intent.kind === 'undo' ? 'undo' : 'attachment'} again</AcButton><AcButton onClick={recovery.dismiss}>Cancel this attempt</AcButton></> : <AcButton onClick={recovery.check}>Check attachment</AcButton>} />}
    {s.message && <details><summary>Details</summary><p>{s.message}</p></details>}
  </section>;
}
